import React from 'react';
import Layout from '../components/layout/Layout';
import { BRAND } from '../data/mock';
import { FileText } from 'lucide-react';

const TermsConditionsPage = () => {
  return (
    <Layout>
      {/* Header */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--peach))]">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[hsl(20,90%,70%)]/10 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[hsl(var(--mint))] mb-6">
            <FileText className="w-8 h-8 text-[hsl(var(--mint-dark))]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
          <p className="text-lg text-gray-600">Last updated: June 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">

            <div className="bg-[hsl(var(--cream))] rounded-2xl p-6 mb-10 border border-[hsl(350,70%,65%)]/20">
              <p className="text-gray-700 leading-relaxed m-0">
                Welcome to <strong>{BRAND.name}</strong>. By accessing our website and placing 
                orders with us, you agree to be bound by the following Terms & Conditions. 
                Please read them carefully before using our services.
              </p>
            </div>

            {[
              {
                title: "1. Acceptance of Terms",
                content: [
                  "By using our website or placing an order, you confirm that you are at least 18 years of age or have parental consent.",
                  "You agree to comply with all applicable laws and regulations when using our services.",
                  "We reserve the right to update these terms at any time. Continued use of our services constitutes acceptance of the updated terms.",
                  "If you disagree with any part of these terms, please do not use our services."
                ]
              },
              {
                title: "2. Products & Orders",
                content: [
                  "All products are made to order. Once production begins, orders cannot be cancelled.",
                  "Product colors may vary slightly from what is displayed on screen due to monitor settings and printing processes.",
                  "We reserve the right to reject any order that contains offensive, illegal, or inappropriate content.",
                  "By placing an order, you confirm that you have the right to use the images and designs you submit.",
                  "Order confirmation will be sent via WhatsApp or email once we receive and verify your order details.",
                  "We reserve the right to modify prices at any time without prior notice. Orders are billed at the price confirmed at the time of order."
                ]
              },
              {
                title: "3. Custom Artwork & Intellectual Property",
                content: [
                  "You must own or have rights to use any photos, designs, or content you submit to us.",
                  "By submitting photos or designs, you grant us a limited license to use them solely for fulfilling your order.",
                  "You must not submit content that infringes on the intellectual property rights of others.",
                  "Artwork created by our team based on your inputs is original work. You receive usage rights for personal use upon full payment.",
                  "We retain the right to use transformed artwork (not original photos) in our portfolio unless you request otherwise in writing.",
                  "We are not responsible for any copyright claims arising from designs submitted by customers."
                ]
              },
              {
                title: "4. Pricing & Payment",
                content: [
                  "All prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes.",
                  "Full payment is required before production begins for all standard orders.",
                  "For bulk corporate orders, 50% advance payment is required with the remaining balance due before shipping.",
                  "We accept payment via UPI, debit/credit cards, and net banking.",
                  "Cash on Delivery (COD) is available for orders above ₹1,500 with an additional fee.",
                  "In case of payment failure, please contact us immediately. Do not attempt multiple payments."
                ]
              },
              {
                title: "5. Production & Delivery",
                content: [
                  "Production begins only after artwork approval from the customer.",
                  "T-shirts are delivered within 5-7 business days and hoodies within 7-10 business days after approval.",
                  "Delivery timelines are estimates and may vary due to factors beyond our control such as courier delays or weather.",
                  "We ship pan-India via reputable courier partners. Tracking information will be shared once dispatched.",
                  "We are not responsible for delays caused by incorrect shipping addresses provided by customers.",
                  "Risk of loss passes to the customer upon handover to the courier."
                ]
              },
              {
                title: "6. Artwork Approval Process",
                content: [
                  "We will share a digital preview of your custom artwork before printing. Your approval is required to proceed.",
                  "You are entitled to up to 2 free revision requests per order.",
                  "Additional revisions beyond 2 may be charged separately.",
                  "Once you approve the artwork, no further changes can be made.",
                  "We aim to share artwork previews within 2-3 business days of receiving your photos and order details.",
                  "Delayed responses to artwork previews may affect delivery timelines."
                ]
              },
              {
                title: "7. Prohibited Content",
                content: [
                  "We strictly do not print content that is offensive, obscene, hateful, or discriminatory.",
                  "Content that violates any individual's privacy or dignity is not permitted.",
                  "Images or designs that infringe on trademarks, copyrights, or other intellectual property rights are prohibited.",
                  "Political or religious content that may cause offence is not accepted.",
                  "We reserve the sole right to determine what constitutes prohibited content and to reject any order accordingly."
                ]
              },
              {
                title: "8. Limitation of Liability",
                content: [
                  "Our total liability to you for any claim arising from our services shall not exceed the amount paid for the specific order in question.",
                  "We are not liable for any indirect, incidental, or consequential damages arising from the use of our products or services.",
                  "We are not responsible for any allergic reactions to our products. Please check fabric details before ordering if you have sensitivities.",
                  "We make no warranties beyond what is expressly stated in these terms."
                ]
              },
              {
                title: "9. Governing Law",
                content: [
                  "These Terms & Conditions are governed by the laws of India.",
                  "Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in India.",
                  "We encourage customers to resolve any issues by contacting us directly at " + BRAND.email + " before pursuing legal remedies."
                ]
              },
              {
                title: "10. Contact Us",
                content: [
                  "For any questions about these Terms & Conditions, please reach out to us:",
                  "Email: " + BRAND.email,
                  "Instagram: @aura_printiqo",
                  "We aim to respond to all queries within 48 hours on business days."
                ]
              }
            ].map((section, index) => (
              <div key={index} className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  {section.title}
                </h2>
                <ul className="space-y-3">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-[hsl(20,90%,70%)] mt-2 flex-shrink-0" />
                      <span className="text-gray-600 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TermsConditionsPage;
